package com.dualremotetest

import kotlin.random.Random

// Online play for two phones anywhere. A small relay server (server/ in this repository) passes text messages between
// a host and a guest who share a 4-letter room code. Both phones then run the same TankMatch in lockstep: every key
// event either player makes is sent to the other phone, both apply the events of each 60 Hz frame in the same order
// at the same frame (a few frames after the press, to cover the trip), and both run that frame only once they have
// both players' events for it. Only key events travel, never the game state; a checksum every second confirms the two
// games still agree. Plain Kotlin in the shared game, so the Android app and the browser (iPhone, iPad, desktop)
// play each other, and it is unit-tested on the JVM.

/** Bumped whenever the messages or the game rules change, so two different versions never play each other. */
const val ONLINE_PROTOCOL = 2

/** The input channels of the two players of an online game (never a real device). */
val ONLINE_DEVICE_IDS = listOf(-3, -4)

/** Frames between a press and the frame it acts in, on both phones: 6 frames = 100 ms for the trip between them. */
const val ONLINE_INPUT_DELAY = 6

/** Room codes: four letters without I, O and Q, which are easy to confuse (with 1, 0 and each other). */
const val ROOM_LETTERS = "ABCDEFGHJKLMNPRSTUVWXYZ"

/** A room code inside pasted text: a 4-letter word of [ROOM_LETTERS] only. */
private val PASTED_CODE = Regex("(?:^|[^A-Z])([$ROOM_LETTERS]{4})(?![A-Z])") // no lookbehind: older iPhone Safari lacks it

/** A place of the room code not filled in yet (after Backspace). */
private const val BLANK = ' '

/** How long CODE COPIED! shows after the host copies the room code. */
const val COPIED_SHOW_MS = 2000L

/** This phone's own settings, put back after an online game used the host's. */
data class OfflineSettings(val difficulty: Difficulty, val moveMode: MoveMode, val smoothingMs: Long)

/** What both phones agree on before an online run: the host's settings, a shared seed and the better high score. */
data class OnlineConfig(
    val seed: Long,
    val difficulty: Difficulty,
    val moveMode: MoveMode,
    val hiScore: Int,
    val delay: Int,
) {
    fun encode() = "$seed|${difficulty.name}|${moveMode.name}|$hiScore|$delay"

    companion object {
        fun decode(parts: List<String>): OnlineConfig? = runCatching {
            OnlineConfig(
                parts[0].toLong(), Difficulty.valueOf(parts[1]), MoveMode.valueOf(parts[2]), parts[3].toInt(),
                parts[4].toInt().coerceIn(1, 60),
            )
        }.getOrNull()
    }
}

enum class LinkState { CONNECTING, OPEN, CLOSED }

/** A connection to the relay. Messages arrive on another thread and wait in a queue; the game polls them per frame. */
interface NetLink {
    val state: LinkState
    /** Why the link closed, for the error screen. */
    val closeReason: String?
    fun send(text: String)
    fun poll(): String?
    fun close()
}

/** What the lobby screens show; drawn by PixelRenderer. */
data class LobbyView(
    val heading: String,
    val lines: List<String>,
    val items: List<String> = emptyList(),
    val cursor: Int = 0,
    /** A room code shown in large letters (the host's code, or the one being typed in). */
    val code: String? = null,
    /** The letter being changed while typing a code; -1 when not typing. */
    val codeCursor: Int = -1,
    val footer: String = "",
    /** A tap or click on [code] copies it (the host's room code, once the room is open). */
    val copyable: Boolean = false,
)

/** The online lobby while the match's phase is LOBBY: keys come from TankMatch.onKey and back(). */
interface Lobby {
    val view: LobbyView
    fun key(dir: Dir?, fire: Boolean)
    fun back()
}

/**
 * One online game in progress. [local] is this phone's player (0 = host, Player 1; 1 = guest, Player 2). Frames
 * 0 until [delay] carry no presses; after running frame f, this phone sends its presses for frame f + [delay].
 */
class LockstepSession(
    private val match: TankMatch,
    link: NetLink,
    val local: Int,
    val delay: Int,
    private val nowMs: () -> Long,
    /**
     * Opens this phone's place in the room again (the relay replaces the old connection). With it, a dropped
     * connection (a phone locked for a moment, a network change) is reopened and the game carries on where it was;
     * without it a dropped connection ends the game.
     */
    private val reopen: (() -> NetLink)? = null,
) {
    private var link = link
    /** How many times the connection was reopened. */
    var reconnects = 0
        private set
    /** This phone's own input messages of the last [RESEND_FRAMES] frames, sent again after a reconnection. */
    private val sentInputs = LinkedHashMap<Int, String>()
    private var lastReopen = 0L
    /** The relay said the other phone's connection dropped; cleared when it is back. */
    private var otherGone = false
    /** The next frame to run. */
    var frame = 0
        private set
    /** Why the session ended ("" for a normal end), or null while it runs. */
    var ended: String? = null
        private set
    /** Frames that could not run yet because the other phone's presses had not arrived. */
    var stalls = 0
        private set

    private val remote = 1 - local
    /** Per player: frame → presses (key code × 2 + 1 for down, 0 for up). */
    private val inputs = arrayOf(HashMap<Int, IntArray>(), HashMap<Int, IntArray>())
    private val pending = ArrayList<Int>()
    private val held = LinkedHashSet<Int>()
    private val ownHashes = HashMap<Int, Long>()
    private val otherHashes = HashMap<Int, Long>()
    private var lastNanos = 0L
    private var acc = 0L
    private var waitingSince = 0L

    init {
        for (f in 0 until delay) {
            inputs[local][f] = IntArray(0)
            sendInput(f, "I|$f|")
        }
    }

    /** A press or release on this phone (any remote, controller, keyboard or the touch controls). */
    fun localKey(keyCode: Int, down: Boolean) {
        val code = when {
            keyCode in FIRE_KEYS -> Keys.KEYCODE_DPAD_CENTER
            Dir.ofKey(keyCode) != null -> keyCode
            else -> return
        }
        if (down == (code in held)) return // a repeat, or the release of something that was never held here
        if (down) held += code else held -= code
        pending += code * 2 + if (down) 1 else 0
    }

    /** BACK on this phone: pauses, resumes or leaves for both, as BACK does offline. */
    fun localBack() {
        pending += Keys.KEYCODE_BACK * 2 + 1
    }

    /** The app went to the background or lost focus: nothing stays held on this phone. */
    fun releaseLocalKeys() {
        for (code in held.toList()) localKey(code, false)
    }

    /** Called once per display frame instead of TankMatch.frame. Runs the frames both phones' presses are in for. */
    fun frame(nowNanos: Long) {
        // The state first: a link that is closed now has already queued every message it received.
        val closed = link.state == LinkState.CLOSED
        while (true) handle(link.poll() ?: break)
        if (ended != null) return
        if (closed) {
            if (reopen == null) {
                end(otherLeft())
                return
            }
            // Reopened at once, then every few seconds while it keeps failing; the game waits meanwhile.
            if (lastReopen == 0L || nowMs() - lastReopen >= REOPEN_EVERY_MS) reconnect()
        }
        if (lastNanos == 0L) lastNanos = nowNanos
        acc += (nowNanos - lastNanos).coerceIn(0, TankMatch.MAX_FRAME_NS)
        lastNanos = nowNanos
        var steps = 0
        while (acc >= TankMatch.STEP_NS && steps < TankMatch.MAX_STEPS && ready(frame)) {
            run(frame++)
            acc -= TankMatch.STEP_NS
            steps++
            if (ended != null) return
        }
        if (acc >= TankMatch.STEP_NS && !ready(frame)) {
            // Waiting for the other phone: no catching up in a burst afterwards.
            stalls++
            acc = TankMatch.STEP_NS
            val now = nowMs()
            if (waitingSince == 0L) waitingSince = now
            if (now - waitingSince > SHOW_WAITING_MS) {
                match.netStatus = if (link.state != LinkState.OPEN) "RECONNECTING..." else "WAITING FOR PLAYER ${remote + 1}"
            }
            // A connection can die without either end hearing of it (a phone that slept, a changed network): after a
            // few seconds of waiting this phone reopens its own place in the room, which also makes the other phone
            // send its latest presses again.
            if (reopen != null && now - waitingSince > REOPEN_EVERY_MS && now - lastReopen >= REOPEN_EVERY_MS) reconnect()
            if (now - waitingSince > GIVE_UP_MS) end(if (otherGone) otherLeft() else "CONNECTION LOST")
        } else {
            waitingSince = 0L
            match.netStatus = null
        }
    }

    /** Leaves the online game from this phone (the other phone is told). */
    fun quit() = end("")

    private fun ready(f: Int) = inputs[0].containsKey(f) && inputs[1].containsKey(f)

    private fun run(f: Int) {
        val t = f * 16L
        match.lockstepNowMs = t
        for (p in 0..1) {
            for (ev in inputs[p].remove(f)!!) {
                if (match.phase == Phase.TITLE || match.phase == Phase.LOBBY) break
                val code = ev shr 1
                if (code == Keys.KEYCODE_BACK) {
                    match.back()
                } else {
                    val action = if (ev and 1 == 1) Keys.ACTION_DOWN else Keys.ACTION_UP
                    match.onKey(KeyInput(code, keyLabel(code), action, 0, ONLINE_DEVICE_IDS[p], 0, 0, 0, t, t, t, t))
                }
            }
        }
        if (match.phase != Phase.TITLE) match.tick()
        val out = f + delay
        inputs[local][out] = pending.toIntArray()
        sendInput(out, "I|$out|" + pending.joinToString(","))
        pending.clear()
        if (f % HASH_EVERY == 0) {
            val h = match.stateHash()
            // Checks lost while a connection was down never pair up: drop the old ones.
            ownHashes.keys.removeAll { it < f - HASH_KEEP_FRAMES }
            otherHashes.keys.removeAll { it < f - HASH_KEEP_FRAMES }
            ownHashes[f] = h
            link.send("C|$f|$h")
            compare(f)
        }
        // Back on the title (QUIT, or the end of the run): both phones get there at the same frame.
        if (match.phase == Phase.TITLE && ended == null) end("")
    }

    private fun sendInput(f: Int, msg: String) {
        sentInputs[f] = msg
        while (sentInputs.size > RESEND_FRAMES) sentInputs.remove(sentInputs.keys.first())
        link.send(msg)
    }

    /** Sends this phone's recent presses again: what the other phone missed while a connection was down. */
    private fun resendInputs() {
        for (msg in sentInputs.values) link.send(msg)
    }

    private fun reconnect() {
        val open = reopen ?: return
        lastReopen = nowMs()
        reconnects++
        link.close()
        link = open()
        // Once the new connection is in the room the relay says "joined" to both phones, and each then resends its
        // recent presses (see handle): links drop what is sent before they open, so nothing is sent here.
    }

    private fun handle(msg: String) {
        if (ended != null) return
        val parts = msg.split('|')
        when (parts[0]) {
            "I" -> if (parts.size >= 3) {
                val f = parts[1].toIntOrNull() ?: return
                if (f < frame || (f < frame + delay && inputs[remote].containsKey(f))) return // a resent frame we have
                inputs[remote][f] = if (parts[2].isEmpty()) IntArray(0) else parts[2].split(',').map { it.toInt() }.toIntArray()
            }
            "C" -> if (parts.size >= 3) {
                val f = parts[1].toIntOrNull() ?: return
                otherHashes[f] = parts[2].toLongOrNull() ?: return
                compare(f)
            }
            "Q" -> end(otherLeft())
            else -> when (ServerMessage.of(msg)) {
                // Without reconnection a dropped player ends the game; with it, the game waits for them (GIVE_UP_MS).
                ServerMessage.LEFT -> if (reopen == null) end(otherLeft()) else otherGone = true
                // Both phones are in the room (again): each sends what the other may have missed.
                ServerMessage.JOINED -> { otherGone = false; resendInputs() }
                else -> Unit
            }
        }
    }

    private fun compare(f: Int) {
        val mine = ownHashes[f] ?: return
        val theirs = otherHashes[f] ?: return
        ownHashes.remove(f)
        otherHashes.remove(f)
        if (mine != theirs) end("OUT OF SYNC - GAME STOPPED")
    }

    private fun otherLeft() = "PLAYER ${remote + 1} LEFT THE GAME"

    private fun end(reason: String) {
        if (ended != null) return
        ended = reason
        link.send("Q")
        link.close()
        match.endOnlineRun(reason.ifEmpty { null })
    }

    companion object {
        const val HASH_EVERY = 60
        const val SHOW_WAITING_MS = 400L
        const val GIVE_UP_MS = 60_000L
        /** How long to wait before reopening a connection, and between tries. */
        const val REOPEN_EVERY_MS = 3_000L
        /** Own presses kept for resending: far more than the other phone can be behind (about [delay] frames). */
        const val RESEND_FRAMES = 240
        const val HASH_KEEP_FRAMES = 600
    }
}

/** Messages from the relay itself (JSON with a "t" field); the players' own messages are passed on unchanged. */
enum class ServerMessage(val type: String) {
    JOINED("joined"), LEFT("left"), FULL("full"), NO_HOST("nohost");

    companion object {
        private val TYPE = Regex(""""t"\s*:\s*"(\w+)"""")
        fun of(msg: String): ServerMessage? {
            if (!msg.startsWith("{")) return null
            val t = TYPE.find(msg)?.groupValues?.get(1) ?: return null
            return entries.firstOrNull { it.type == t }
        }
    }
}

/**
 * Title → ONLINE GAME: the lobby (create a room and share its code, or type a code in), then the lockstep session.
 * [relayUrl] is the relay server's address (https://…); empty when this build has none. [connect] opens a WebSocket.
 */
class OnlinePlay(
    private val match: TankMatch,
    private val relayUrl: String,
    private val connect: (String) -> NetLink,
    private val nowMs: () -> Long,
    private val random: Random = Random.Default,
) : Lobby {
    private enum class Screen { MENU, NOT_SET_UP, HOSTING, ENTER_CODE, JOINING, ERROR }

    var session: LockstepSession? = null
        private set
    private var link: NetLink? = null
    private var screen = Screen.MENU
    private var cursor = 0
    private var code = ""
    private val entry = CharArray(4) { 'A' }
    private var entryPos = 0
    /** Letters typed so far on a keyboard (0..4): the next one goes there, and Backspace clears the one before. */
    private var typed = 0
    private var error = emptyList<String>()
    private var errorBackTo = Screen.MENU
    private var connectStarted = 0L
    private var wasOpen = false
    private var hostTries = 0
    /** The guest has said hello and the host has not started yet (host side). */
    private var started = false
    /** When the room code was last copied (nowMs), to show CODE COPIED for a moment. */
    private var copiedAt: Long? = null

    init {
        match.onOnline = ::open
    }

    override val view: LobbyView
        get() = when (screen) {
            Screen.MENU -> LobbyView(
                "ONLINE GAME", listOf("PLAY TOGETHER ON TWO DEVICES,", "WHEREVER YOU BOTH ARE"),
                listOf("CREATE ROOM", "JOIN ROOM", "BACK"), cursor, footer = "FIRE: SELECT  BACK: TITLE",
            )
            Screen.NOT_SET_UP -> LobbyView(
                "ONLINE GAME", listOf("ONLINE PLAY IS NOT SET UP", "IN THIS VERSION: IT HAS NO", "GAME SERVER ADDRESS"),
                listOf("BACK"), 0,
            )
            Screen.HOSTING -> {
                val open = link?.state == LinkState.OPEN
                val canTap = match.touchDevice || match.keyboardHints
                val copyLine = when {
                    !open || !canTap -> null
                    copiedAt?.let { nowMs() - it < COPIED_SHOW_MS } == true -> "CODE COPIED!"
                    match.keyboardHints -> "CLICK THE CODE TO COPY IT" // a computer with a mouse
                    else -> "TAP THE CODE TO COPY IT"
                }
                LobbyView(
                    "ROOM CODE",
                    if (open) listOfNotNull("TELL PLAYER 2 THIS CODE", copyLine, "WAITING FOR PLAYER 2...") else listOf("CONNECTING..."),
                    code = code, footer = "YOU ARE PLAYER 1   BACK: CANCEL", copyable = open,
                )
            }
            Screen.ENTER_CODE -> LobbyView(
                "JOIN ROOM", listOf("ENTER THE ROOM CODE", "FROM PLAYER 1'S SCREEN"),
                code = entry.concatToString(), codeCursor = entryPos,
                footer = if (match.keyboardHints) "TYPE OR PASTE IT  ENTER: JOIN" else "UP/DOWN: LETTER  FIRE: JOIN",
            )
            Screen.JOINING -> LobbyView("JOIN ROOM", listOf("CONNECTING..."), code = entry.concatToString(), footer = "BACK: CANCEL")
            Screen.ERROR -> LobbyView("ONLINE GAME", error, listOf("OK"), 0)
        }

    /** Opens the lobby from the title. */
    fun open() {
        cursor = 0
        screen = if (relayUrl.isBlank()) Screen.NOT_SET_UP else Screen.MENU
        match.openLobby(this)
    }

    override fun key(dir: Dir?, fire: Boolean) {
        when (screen) {
            Screen.MENU -> when {
                dir == Dir.UP -> cursor = (cursor + 2) % 3
                dir == Dir.DOWN -> cursor = (cursor + 1) % 3
                fire -> when (cursor) {
                    0 -> { hostTries = 0; host() }
                    1 -> { screen = Screen.ENTER_CODE; entryPos = 0; typed = 0 }
                    else -> match.closeLobby()
                }
            }
            Screen.ENTER_CODE -> when {
                dir == Dir.UP -> step(1)
                dir == Dir.DOWN -> step(-1)
                dir == Dir.LEFT -> { entryPos = (entryPos + 3) % 4; typed = entryPos }
                dir == Dir.RIGHT -> { entryPos = (entryPos + 1) % 4; typed = entryPos }
                fire -> if (BLANK !in entry) join()
            }
            Screen.NOT_SET_UP -> if (fire) match.closeLobby()
            Screen.ERROR -> if (fire) { screen = errorBackTo; cursor = 0 }
            Screen.HOSTING, Screen.JOINING -> Unit
        }
    }

    override fun back() {
        when (screen) {
            Screen.MENU, Screen.NOT_SET_UP -> match.closeLobby()
            Screen.ENTER_CODE, Screen.ERROR -> { screen = Screen.MENU; cursor = 0 }
            Screen.HOSTING, Screen.JOINING -> { disconnect(); screen = Screen.MENU; cursor = 0 }
        }
    }

    /** Called once per display frame instead of TankMatch.frame. */
    fun frame(nowNanos: Long) {
        session?.let { s ->
            s.frame(nowNanos)
            if (s.ended != null) {
                session = null
                link = null
            }
            return
        }
        match.frame(nowNanos)
        if (match.phase == Phase.LOBBY) pollLobby()
    }

    /** A key on this phone during an online game. Returns false when no game is running. */
    fun sessionKey(keyCode: Int, down: Boolean): Boolean {
        val s = session ?: return false
        if (keyCode == Keys.KEYCODE_BACK || keyCode == Keys.KEYCODE_ESCAPE) {
            if (!down) s.localBack()
        } else {
            s.localKey(keyCode, down)
        }
        return true
    }

    /**
     * The room code to copy when the player taps or clicks the picture at ([x], [y]) (0..256 × 0..224, side panels
     * excluded), or null when that point is not on a copyable code.
     */
    fun codeAt(x: Int, y: Int): String? =
        if (session == null && match.phase == Phase.LOBBY && match.lobby === this) PixelRenderer.lobbyCodeAt(match, x, y) else null

    /** JOIN ROOM is showing: a keyboard's letters type the code (see [typeLetter], [eraseLetter], [pasteCode]). */
    val typingCode: Boolean
        get() = session == null && match.phase == Phase.LOBBY && match.lobby === this && screen == Screen.ENTER_CODE

    /** A letter typed on a keyboard while [typingCode]: it fills the current place and moves on to the next. */
    fun typeLetter(ch: Char) {
        if (!typingCode) return
        val c = ch.uppercaseChar()
        if (c !in ROOM_LETTERS) return // I, O and Q never appear in a code
        if (typed == 0) entry.fill(BLANK) // the first typed letter starts a fresh code
        if (typed >= 4) return
        entry[typed] = c
        typed++
        entryPos = minOf(typed, 3)
    }

    /** Backspace while [typingCode]: clears the last typed letter. */
    fun eraseLetter() {
        if (!typingCode) return
        if (typed == 0) typed = 4 - entry.reversed().takeWhile { it == BLANK }.size // a code set with the arrows
        if (typed == 0) return
        typed--
        entry[typed] = BLANK
        entryPos = typed
    }

    /**
     * Pasted text (for example a code copied from a message): a 4-letter word made of code letters fills JOIN ROOM. Works from
     * the lobby menu too, which then opens JOIN ROOM with the code filled in; FIRE or Enter joins.
     */
    fun pasteCode(text: String) {
        if (session != null || match.phase != Phase.LOBBY || match.lobby !== this) return
        if (screen != Screen.MENU && screen != Screen.ENTER_CODE) return
        // A code is copied in capitals: prefer a word already in capitals ("Join my game: KBSV" → KBSV, not GAME),
        // then the last candidate of the text in any case.
        val found = (PASTED_CODE.find(text) ?: PASTED_CODE.findAll(text.uppercase()).lastOrNull())?.groupValues?.get(1) ?: return
        found.forEachIndexed { i, c -> entry[i] = c }
        screen = Screen.ENTER_CODE
        entryPos = 3
        typed = 4
    }

    /** The platform has put the room code on the clipboard: say so on screen for a moment. */
    fun codeCopied() {
        copiedAt = nowMs()
    }

    /** This phone's app went to the background or lost focus. */
    fun onLocalFocusLost() {
        session?.releaseLocalKeys()
    }

    private fun step(by: Int) {
        if (entry[entryPos] == BLANK) { // a cleared place starts again from A
            entry[entryPos] = ROOM_LETTERS[0]
            return
        }
        val i = ROOM_LETTERS.indexOf(entry[entryPos]).coerceAtLeast(0)
        entry[entryPos] = ROOM_LETTERS[(i + by + ROOM_LETTERS.length) % ROOM_LETTERS.length]
    }

    private fun host() {
        copiedAt = null
        code = roomCode(hostTries)
        started = false
        open(Screen.HOSTING, "host")
    }

    private fun join() {
        code = entry.concatToString()
        open(Screen.JOINING, "guest")
    }

    private fun open(next: Screen, role: String) {
        disconnect()
        screen = next
        connectStarted = nowMs()
        wasOpen = false
        link = connect(roomUrl(relayUrl, code, role))
    }

    private fun disconnect() {
        link?.close()
        link = null
    }

    private fun fail(vararg lines: String, backTo: Screen = Screen.MENU) {
        disconnect()
        error = lines.toList()
        errorBackTo = backTo
        screen = Screen.ERROR
    }

    private fun pollLobby() {
        val l = link ?: return
        if (screen != Screen.HOSTING && screen != Screen.JOINING) return
        // The state first: a link that is closed now has already queued every message it received (a "nohost" before
        // the server closes, for example), so those are read before the closed link is reported.
        val state = l.state
        if (state == LinkState.OPEN) wasOpen = true
        while (true) {
            val msg = l.poll() ?: break
            val parts = msg.split('|')
            when (ServerMessage.of(msg)) {
                ServerMessage.JOINED -> if (screen == Screen.JOINING) l.send("H|$ONLINE_PROTOCOL|${match.hiScore}")
                // Rooms are handed out in order: AAAA, then AAAB if that one is in use, and so on.
                ServerMessage.FULL -> if (screen == Screen.HOSTING && ++hostTries < MAX_HOST_TRIES) { host(); return } else {
                    if (screen == Screen.HOSTING) fail("ALL ROOMS ARE BUSY.", "TRY AGAIN IN A MOMENT.")
                    else fail("THAT ROOM ALREADY HAS", "TWO PLAYERS", backTo = Screen.ENTER_CODE)
                    return
                }
                ServerMessage.NO_HOST -> { fail("NO GAME WITH THE CODE $code.", "CHECK THE CODE ON", "PLAYER 1'S SCREEN", backTo = Screen.ENTER_CODE); return }
                ServerMessage.LEFT -> if (screen == Screen.JOINING) { fail("PLAYER 1 LEFT THE ROOM", backTo = Screen.MENU); return }
                null -> when (parts[0]) {
                    "H" -> if (screen == Screen.HOSTING && !started) {
                        if (parts.getOrNull(1)?.toIntOrNull() != ONLINE_PROTOCOL) {
                            l.send("X|$ONLINE_PROTOCOL")
                            fail("THE OTHER DEVICE HAS A", "DIFFERENT VERSION OF", "THE GAME. UPDATE BOTH.")
                            return
                        }
                        started = true
                        val cfg = OnlineConfig(
                            random.nextLong(), match.difficulty, match.moveMode,
                            maxOf(match.hiScore, parts.getOrNull(2)?.toIntOrNull() ?: 0), ONLINE_INPUT_DELAY,
                        )
                        l.send("G|$ONLINE_PROTOCOL|${cfg.encode()}")
                        begin(cfg, 0, l)
                        return
                    }
                    "G" -> if (screen == Screen.JOINING) {
                        val cfg = OnlineConfig.decode(parts.drop(2))
                        if (parts.getOrNull(1)?.toIntOrNull() != ONLINE_PROTOCOL || cfg == null) {
                            fail("THE OTHER DEVICE HAS A", "DIFFERENT VERSION OF", "THE GAME. UPDATE BOTH.")
                            return
                        }
                        begin(cfg, 1, l)
                        return
                    }
                    "X" -> { fail("THE OTHER DEVICE HAS A", "DIFFERENT VERSION OF", "THE GAME. UPDATE BOTH."); return }
                }
            }
        }
        val backTo = if (screen == Screen.JOINING) Screen.ENTER_CODE else Screen.MENU
        when {
            state == LinkState.CLOSED && wasOpen -> fail("THE CONNECTION TO THE", "GAME SERVER WAS LOST", backTo = backTo)
            state == LinkState.CLOSED -> fail("CAN'T REACH THE", "GAME SERVER. CHECK THE", "INTERNET CONNECTION.", backTo = backTo)
            state == LinkState.CONNECTING && nowMs() - connectStarted > CONNECT_TIMEOUT_MS ->
                fail("CAN'T REACH THE", "GAME SERVER. CHECK THE", "INTERNET CONNECTION.", backTo = backTo)
        }
    }

    private fun begin(cfg: OnlineConfig, local: Int, l: NetLink) {
        screen = Screen.MENU
        match.lobby = null
        match.startOnlineRun(cfg, local)
        val role = if (local == 0) "host" else "guest"
        val room = code
        session = LockstepSession(match, l, local, cfg.delay, nowMs) { connect(roomUrl(relayUrl, room, role, rejoin = true)) }
    }

    companion object {
        const val CONNECT_TIMEOUT_MS = 15_000L

        /** wss://host/room/CODE?role=host|guest&v=N from the relay's https:// (or http:// for a local test) address. */
        /** How many rooms the host tries, in order, before giving up. */
        const val MAX_HOST_TRIES = 100

        /** The [i]th room code in order: 0 → AAAA, 1 → AAAB, …, 22 → AAAZ, 23 → AABA. */
        fun roomCode(i: Int): String {
            val n = ROOM_LETTERS.length
            var rest = i
            val chars = CharArray(4)
            for (place in 3 downTo 0) {
                chars[place] = ROOM_LETTERS[rest % n]
                rest /= n
            }
            return chars.concatToString()
        }

        fun roomUrl(relayUrl: String, code: String, role: String, rejoin: Boolean = false): String {
            val base = relayUrl.trim().trimEnd('/')
                .replaceFirst(Regex("^https://"), "wss://").replaceFirst(Regex("^http://"), "ws://")
            return "$base/room/$code?role=$role&v=$ONLINE_PROTOCOL" + if (rejoin) "&rejoin=1" else ""
        }
    }
}
